import { useEffect, useRef } from 'react';
import { BroadcastChannel } from 'broadcast-channel';

import type { YandexAuthConfig as SocialConfig } from 'src/components/AuthNew/utils/socialNetworkUtils';

const createAuthYandexUrl = (socialConfig: SocialConfig) => {
  const params = [
    'response_type=token',
    `client_id=${socialConfig.id}`,
    'display=popup',
    `redirect_uri=${window.location.origin}/login/yandex`,
  ].join('&');
  return ['https://oauth.yandex.ru/authorize', params].join('?');
};

type AuthYandexCodeError = {
  success: false;
  error: {
    error?: string;
    error_reason?: string;
    error_description?: string;
  };
};
type AuthYandexCodeMessageSuccess = {
  success: true;
  access_token: string;
  expires_in: number;
};
type AuthYandexCodeMessageResult =
  | AuthYandexCodeError
  | AuthYandexCodeMessageSuccess;

type OnSuccess = (payload: AuthYandexCodeMessageResult) => void;
type OnError = () => void;

/**
 * @desc процесс авторизации следующий:
 * 1. в новом окне открывается ссылка для авторизации на стороне Yandex
 * 2. создается Broadcast канал для обмена сообщениями между окном инициатором авторизации
 * и окном, где происходит запрос кода Yandex
 * 3. после подтверждения доступа, Yandex перенаправляет на redirect_url из ссылки и добавляет code или ошибки в параметры
 * 4. на странице шаблона (app/views/social_login/yandex.haml) скрипт считывает code или ошибки и отправляет их по Broadcast каналу
 * 5. хук передает полученные данные вызываемому методу.
 * */
export const useYandexAuth = () => {
  /** ссылка для авторизации в Yandex */
  /** полученный от Yandex код авторизации */
  const authCodeRef = useRef<AuthYandexCodeMessageResult | undefined>();
  const authChanel = useRef<BroadcastChannel>();

  const resetAuthCodeRef = (): void => {
    authCodeRef.current = undefined;
  };

  /** открыть окно для авторизации через Yandex */
  const openYandexAuthPopup = (url: string): Window | null =>
    window.open(url, '_blank', 'popup');

  /** прослушивание закрытия окна авторизации Yandex */
  const listenCloseYandexAuthPopup =
    (onError: OnError) =>
    (instance: Window | null): void => {
      if (!instance) {
        return;
      }
      const intID = setInterval(() => {
        if (instance?.closed) {
          clearInterval(intID);
          if (!authCodeRef.current || !authCodeRef.current?.success) {
            onError();
          }
        }
      }, 500);
    };

  /** обработка ответа из результирующего окна авторизации через Yandex */
  const listenYandexAuthChanel =
    (onSuccess: OnSuccess, onError: OnError) =>
    (event: AuthYandexCodeMessageResult) => {
      if (!event?.success) {
        // eslint-disable-next-line no-console
        console.error('YANDEX AUTH  NOT SUCCESS: ', event);
        onError();
        return;
      }
      authCodeRef.current = event;
      onSuccess(event);
    };

  /**
   * Открыть канал для общения между окном инициатором авторизации
   * и окном в котором происходит получение кода от Yandex
   * */
  const openYandexAuthChanel = (listener) => {
    const chanel = new BroadcastChannel('auth-yandex-code');
    authChanel.current = chanel;
    chanel.addEventListener('message', listener);
  };

  const closeYandexAuthChanel = () => {
    authChanel.current?.close();
  };

  const handleYandexLogin = (
    socialConfig: SocialConfig,
    onSuccess: OnSuccess,
    onError: OnError,
  ): void => {
    try {
      closeYandexAuthChanel();
      resetAuthCodeRef();

      const authURL = createAuthYandexUrl(socialConfig);
      listenCloseYandexAuthPopup(onError)(openYandexAuthPopup(authURL));
      openYandexAuthChanel(listenYandexAuthChanel(onSuccess, onError));
    } catch (e) {
      console.log(e);
    }
  };

  /** закрытие канала обмена сообщениями */
  useEffect(() => closeYandexAuthChanel, []);

  return {
    handleYandexLogin,
  };
};
